import { useState } from 'react';
import { useLocationStore } from '@/stores/useLocationStore';
import { useCartStore } from '@/stores/useCartStore';
import { DateRange } from '@/types';
import { addDays, format } from 'date-fns';
import { ContextChangeWarningModal } from './ContextChangeWarningModal';

interface DateSelectContentProps {
    onClose?: () => void;
}

export const DateSelectContent = ({ onClose }: DateSelectContentProps) => {
    const { location, dateRange, setDateRange } = useLocationStore();
    const cartItems = useCartStore((state) => state.items);
    const itemCount = useCartStore((state) => state.itemCount());
    const validateCart = useCartStore((state) => state.validateCart);

    // Warning modal state
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [pendingDateRange, setPendingDateRange] = useState<DateRange | null>(null);

    const formatDateRange = (range: DateRange | null) => {
        if (!range?.from) return undefined;
        const fromStr = format(range.from, 'MMM d');
        const toStr = range.to ? format(range.to, 'MMM d') : '';
        return toStr ? `${fromStr} - ${toStr}` : fromStr;
    };

    const handleDateChange = (newRange: DateRange) => {
        // Check if dates actually changed
        const datesChanged =
            newRange.from?.getTime() !== dateRange.from?.getTime() ||
            newRange.to?.getTime() !== dateRange.to?.getTime();

        if (cartItems.length > 0 && datesChanged) {
            // Show warning modal
            setPendingDateRange(newRange);
            setShowWarningModal(true);
        } else {
            setDateRange(newRange);
        }
    };

    const handleConfirmDateChange = async () => {
        if (pendingDateRange) {
            setDateRange(pendingDateRange);

            // Trigger cart validation if we have location and both dates set
            if (location?.id && pendingDateRange.from && pendingDateRange.to && cartItems.length > 0) {
                const startDate = format(pendingDateRange.from, 'yyyy-MM-dd');
                const endDate = format(pendingDateRange.to, 'yyyy-MM-dd');
                await validateCart(startDate, endDate, location.id);
            }

            onClose?.();
        }
        setPendingDateRange(null);
    };

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Use local noon (no Z suffix) so .toISOString() stays on the same calendar date in any timezone
        const date = e.target.value ? new Date(`${e.target.value}T12:00:00`) : undefined;
        handleDateChange({ ...dateRange, from: date });
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = e.target.value ? new Date(`${e.target.value}T12:00:00`) : undefined;
        handleDateChange({ ...dateRange, to: date });
    };

    const hasDates = !!(dateRange.from || dateRange.to);

    const handleClearDates = () => {
        handleDateChange({ from: undefined, to: undefined });
        onClose?.();
    };

    return (
        <>
            <div className="space-y-4 p-4">
                <div className="grid gap-2">
                    <label className="text-sm font-medium text-foreground">Start Date</label>
                    <input
                        type="date"
                        className="w-full rounded-md border border-input px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
                        min={new Date().toISOString().split('T')[0]}
                        onChange={handleStartDateChange}
                        value={dateRange.from ? dateRange.from.toISOString().split('T')[0] : ''}
                    />
                </div>
                <div className="grid gap-2">
                    <label className="text-sm font-medium text-foreground">End Date</label>
                    <input
                        type="date"
                        className="w-full rounded-md border border-input px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
                        min={dateRange.from ? addDays(dateRange.from, 1).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                        onChange={handleEndDateChange}
                        value={dateRange.to ? dateRange.to.toISOString().split('T')[0] : ''}
                    />
                </div>

                {hasDates ? (
                    <button
                        type="button"
                        onClick={handleClearDates}
                        className="w-full rounded-md border border-border/60 bg-secondary/60 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                        Clear dates
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => onClose?.()}
                        className="w-full rounded-md px-3 py-2 text-sm text-muted-foreground/70 transition-colors hover:text-muted-foreground"
                    >
                        I&apos;ll decide later
                    </button>
                )}
            </div>

            {/* Warning Modal */}
            <ContextChangeWarningModal
                isOpen={showWarningModal}
                onClose={() => {
                    setShowWarningModal(false);
                    setPendingDateRange(null);
                }}
                onConfirm={handleConfirmDateChange}
                changeType="dates"
                itemCount={itemCount}
                currentDates={formatDateRange(dateRange)}
                newDates={formatDateRange(pendingDateRange)}
            />
        </>
    );
};
